import React from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Paper,
  Stack,
  IconButton,
  Tooltip // Added Tooltip
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import WeekendIcon from '@mui/icons-material/Weekend'; // Icon for Virtual Staging
import PaletteIcon from '@mui/icons-material/Palette'; // Added PaletteIcon
import { useState, useEffect } from 'react'; // Import useState and useEffect

const roomTypes = [
  { value: 'Living Room', label: 'Oturma Odası' },
  { value: 'Bedroom', label: 'Yatak Odası' },
  { value: 'Kitchen', label: 'Mutfak' },
  { value: 'Bathroom', label: 'Banyo' },
  { value: 'Dining Room', label: 'Yemek Odası' },
];

const styles = [
  { value: 'scandinavian', label: 'İskandinav' },
  { value: 'contemporary', label: 'Çağdaş' },
  { value: 'modern', label: 'Modern' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'bohemian', label: 'Bohem' },
  { value: 'japandi', label: 'Japandi' },
  { value: 'parisian', label: 'Paris' },
  { value: 'eclectic', label: 'Eklektik' },
];

const colorThemes = [
  { value: 'original', label: 'Orijinal', hex: 'transparent' }, // Use transparent for original
  { value: 'white', label: 'Beyaz', hex: '#FFFFFF' },
  { value: 'black', label: 'Siyah', hex: '#000000' },
  { value: 'grey', label: 'Gri', hex: '#9E9E9E' },
  { value: 'blue', label: 'Mavi', hex: '#2196F3' },
  { value: 'green', label: 'Yeşil', hex: '#4CAF50' },
  { value: 'red', label: 'Kırmızı', hex: '#F44336' },
  { value: 'yellow', label: 'Sarı', hex: '#FFEB3B' },
  { value: 'brown', label: 'Kahverengi', hex: '#795548' },
  { value: 'purple', label: 'Mor', hex: '#9C27B0' },
  { value: 'pink', label: 'Pembe', hex: '#E91E63' },
  { value: 'orange', label: 'Turuncu', hex: '#FF9800' },
];

// Styled components (similar to CropToolOptions)
const OptionsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  width: '100%',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  animation: 'fadeIn 0.3s ease-out',
  maxHeight: '90vh',
  overflowY: 'auto',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const ColorButton = styled(Box)(({ theme, color, selected }) => ({ // Added selected prop
  width: 32, // Slightly smaller
  height: 32,
  borderRadius: '50%',
  backgroundColor: color,
  border: selected
    ? `3px solid ${theme.palette.primary.main}` // Highlight selected
    : `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 0 0 4px ${theme.palette.primary.main}33` // Use theme color with alpha
  },
  // Add inner shadow for 'original' to make it visible
  ...(color === 'transparent' && {
    boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
    backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
    backgroundSize: '10px 10px',
    backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
  })
}));

const ColorGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(0.5), // Reduced margin
  marginBottom: theme.spacing(1), // Reduced margin
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.02)',
  borderRadius: theme.spacing(1.5),
}));


const PreviewBox = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1), // Reduced margin
  display: 'flex',
  flexDirection: 'column', // Stack icon and text
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  minHeight: 100, // Reduced height
  overflow: 'hidden',
  position: 'relative',
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const OptionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '1.2rem'
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  padding: 4,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
  },
  zIndex: 10 // Ensure button is above other elements
}));


const VirtualStagingToolOptions = ({ settings: initialSettings, onChange, onApply }) => {
  // Internal state to manage options locally
  const [internalSettings, setInternalSettings] = useState(initialSettings);

  // Sync internal state when the initialSettings prop changes (e.g., reset)
  useEffect(() => {
    setInternalSettings(initialSettings);
  }, [initialSettings]);

  // Function to update internal state and notify parent
  const updateSetting = (newSettings) => {
    setInternalSettings(newSettings); // Update internal state first
    if (typeof onChange === 'function') {
      onChange(newSettings); // Notify parent (ImageEditor)
    }
  };


  // Close handler passed down from ImageEditor
  const handleClose = () => {
    if (typeof onApply === 'function') {
      // Pass the *internal* settings on close/cancel
      onApply(false, internalSettings);
    }
  };

  const handleRoomTypeChange = (event) => {
    updateSetting({ ...internalSettings, roomType: event.target.value });
  };

  const handleStyleChange = (event) => {
    updateSetting({ ...internalSettings, style: event.target.value });
  };

  const handleColorThemeChange = (value) => {
    updateSetting({ ...internalSettings, colorTheme: value });
  };


  return (
    <OptionsPaper elevation={4}>
      <Box sx={{ position: 'relative' }}>
        <CloseButton onClick={() => onApply(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>

        <Stack spacing={2}>
          <Box>
            <OptionTitle>
              <WeekendIcon /> Sanal Dizayn
            </OptionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Boş odaları seçtiğiniz stile göre mobilyalarla doldurun.
            </Typography>
          </Box>

          <PreviewBox>
             <WeekendIcon sx={{ fontSize: 40, mb: 1 }} />
             <Typography variant="caption">
               Oda tipi, stil ve renk teması seçin.
             </Typography>
          </PreviewBox>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="room-type-label">Oda Tipi</InputLabel>
                <Select
              labelId="room-type-label"
              value={internalSettings.roomType} // Use internal state
              label="Oda Tipi"
              onChange={handleRoomTypeChange}
            >
              {roomTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="style-label">Stil</InputLabel>
            <Select
              labelId="style-label"
              value={internalSettings.style} // Use internal state
              label="Stil"
              onChange={handleStyleChange}
            >
              {styles.map((style) => (
                <MenuItem key={style.value} value={style.value}>
                  {style.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Color Theme Selection */}
      <Box>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
           <PaletteIcon fontSize="small" sx={{ mr: 0.5 }} /> Renk Teması
        </Typography>
        <ColorGrid container spacing={1}>
          {colorThemes.map((color) => (
            <Grid item key={color.value}>
              <Tooltip title={color.label}>
                <ColorButton
                  color={color.value === 'original' ? 'transparent' : color.hex} // Use hex, handle original
                  selected={internalSettings.colorTheme === color.value} // Use internal state
                  onClick={() => handleColorThemeChange(color.value)}
                />
              </Tooltip>
            </Grid>
          ))}
        </ColorGrid>
      </Box>


      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            console.log("VirtualStagingToolOptions: Applying with internal settings:", internalSettings); // Log internal settings
            onApply(true, internalSettings); // Pass true and *internal* settings
          }}
          size="medium"
        >
          Oluştur (8 Kredi)
        </Button>
      </Box>
    </Stack>
   </Box>
  </OptionsPaper>
  );
};

export default VirtualStagingToolOptions;
